// OutstandingAccountDetailPage.tsx
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAccountDetails, usePaymentAgreementDetail } from "../../hooks";

import { ActivePaymentAgreementCard } from "./ActivePaymentAgreementCard";
import { AgreementHistoryTable } from "./AggrementHistory";
import { BillHistoryTable } from "./BillHistory";
import ConsumerOverviewCards from "./ConsumerOutstandingCard";
import { CreatePaymentAgreementModal } from "./CreatePaymentAgreementModal";
import { AuditTrailSummary } from "../auditTrail/summary/summary";
import { AuditTrailList } from "../auditTrail/list/list";
import { Tabs } from "@shared/ui/tabs";
import { Card } from "@shared/ui/card";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";

export function OutstandingAccountDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const queryParams = {
    remoteUtilityId: remoteUtilityId || 0,
    consumerNumber: params.consumerNumber || "",
  };
  const { data: account } = useAccountDetails(queryParams);
  const { data: activeAgreement } = usePaymentAgreementDetail({
    remoteUtilityId: Number(remoteUtilityId),
    remoteBillId: Number(account?.billData?.id),
  });

  const [showAgreementModal, setShowAgreementModal] = useState(false);

  const handleCreateAgreement = () => {
    setShowAgreementModal(true);
  };

  const urlMapping = {
    billHistory: "billHistory",
    agreement: "agreement",
    activityLogs: "activityLogs",
  };

  const tabComponents = {
    billHistory: {
      label: "Bill History",
      component: (
        <div className="space-y-4">
          <BillHistoryTable />
        </div>
      ),
    },
    agreement: {
      label: "Agreement History",
      component: (
        <div className="space-y-4">
          <AgreementHistoryTable />
        </div>
      ),
    },
    activityLogs: {
      label: "Activity Logs",
      component: (
        <div className="space-y-4">
          <TimelineTab
            remoteUtilityId={Number(remoteUtilityId)}
            entityId={account?.id.toString()}
            actionType="outstanding"
          />
        </div>
      ),
    },
  };

  return (
    <div className="space-y-6">
      <ConsumerOverviewCards
        consumer={{
          id: account?.id ?? "NA",
          name: account?.firstName + " " + account?.lastName,
          accountNo: account?.consumerNo ?? "NA",
          status: account?.statusDisplay ?? "NA",
          riskLevel: account?.billData?.risk ?? "NA",
          phone: account?.contactNumber ?? "NA",
          email: account?.email ?? "—",
          address: [
            account?.serviceAddress?.region ?? "NA",
            account?.serviceAddress?.county ?? "NA",
            account?.serviceAddress?.country ?? "NA",
          ].join(", "),
        }}
        outstanding={{
          totalOutstanding: account?.billData?.outstandingBalance ?? 0,
          pendingBills: account?.billData?.pendingBillsCount ?? 0,
          lastPaymentAmount: account?.billData?.lastPayAmount,
          lastPaidDate: account?.billData?.lastPaymentDate,
        }}
        onViewProfile={() =>
          navigate(`/cx/consumer-management/${params.consumerNumber}`)
        }
        onPayNow={() =>
          navigate("/cx/payments/create", {
            state: {
              consumerNumber: params.consumerNumber,
              paymentType: "2", // 2 for bill payment as per choices
            },
          })
        }
        onCreateAgreement={handleCreateAgreement}
      />

      {/* Tabbed Section: Bill History | Agreement | Activity Logs */}
      <div className="mt-2">
        <Tabs
          defaultValue="billHistory"
          className="w-full"
          tabsListClassName="grid w-full grid-cols-3 mb-6"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          level={0}
        />
      </div>

      {/* Create Payment Agreement Modal */}
      <CreatePaymentAgreementModal
        isOpen={showAgreementModal}
        onClose={() => setShowAgreementModal(false)}
        totalOutstanding={account?.billData?.outstandingBalance ?? 0}
        latestBillAmount={account?.billData?.billAmount}
        consumerId={account?.id}
        remoteBillId={account?.billData?.id}
      />
    </div>
  );
}
